import {
  APIV1Version,
  ClientType,
  getStoreName,
} from '@buncha/common/types/common';
import {
  ActivityIndicator,
  NativeScrollEvent,
  NativeSyntheticEvent,
  RefreshControl,
  RefreshControlProps,
} from 'react-native';
import {
  AsyncStatus,
  B2BScreenParam,
  Order,
  OrderInfoModes,
  OrderVerifyStatus,
  OrdersForUpdation,
} from '@buncha/B2B/types';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H5Text, H6Text} from '@buncha/components/core/text';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import {MainScreens, TabScreens} from '@buncha/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useCancelOrder,
  useCheckAsyncOrderStatus,
  useGetOrdersForValidationAndUpdationCheckout,
  useUpdateB2BOrder,
} from '@buncha/B2B/hooks/B2BChekout';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';
import {useIsFocused, useRoute} from '@react-navigation/native';

import {Analytics} from '@buncha/config/analytics/analytics';
import CancelOrderModal from '../CancelOrderModal';
import CheckoutItem from '../common/CheckoutItem';
import {CustomFlatList} from '@buncha/components/core/list';
import EmptyOrders from '../common/EmptyOrders';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import QRCode from '@buncha/components/core/icon/QRCode';
import {Touchable} from '@buncha/components/core/touchable';
import {b2bNav} from '@buncha/B2B/navigation';
import {navigation} from '@buncha/config/navigation';
import {styles} from './styles';
import {useB2BCheckoutContext} from '@buncha/B2B/hooks/B2BCheckoutReducer';
import {useGetLocale} from '@buncha/localization/hooks';

interface ApprovalProps {
  loading: boolean;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

const Approval: React.FC<ApprovalProps> = props => {
  const {loading} = props;
  const {state, dispatch} = useB2BCheckoutContext();
  const theme = useColorThemeType();
  const [getOrdersForValidationAndUpdation, statusState] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const {cancelOrder} = useCancelOrder();
  const {title, body, button} =
    useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const muliFont = useFontTheme('muli');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [updateB2BOrder] = useUpdateB2BOrder(APIV1Version.V100);
  const updationItemItems = state.ordersForUpdation ?? [];
  const validationItems = state.ordersForValidation ?? [];
  const routeParams = useRoute()?.params as B2BScreenParam | undefined;
  const cancelledOrderIds = updationItemItems
    .filter(item => item.status === OrderVerifyStatus.updationFailed)
    .map(item => item?.order?.id);
  const {checkAsyncOrderStatus, data} = useCheckAsyncOrderStatus(
    APIV1Version.V100,
  );
  const scanFinished = useMemo(() => {
    const scanInProgress = updationItemItems.filter(
      item => item.status !== OrderVerifyStatus.scanSuccessful,
    )?.length;
    if (updationItemItems?.length && !scanInProgress) return true;
    return false;
  }, [updationItemItems]);

  const isScannedEnabled = updationItemItems.find(
    item => item.status === OrderVerifyStatus.updationInProgress,
  );
  const isFocus = useIsFocused();
  const showButton = [
    OrderVerifyStatus.updationFailed,
    OrderVerifyStatus.updationDone,
  ];
  const isUpdate = data?.success?.find(
    item => item.status !== AsyncStatus.pending,
  );
  const reFetchOrderIds = data?.success
    ?.filter(item => item.status === AsyncStatus.failed)
    .map(item => item.orderId);

  const checkForUpdates = useCallback(() => {
    const newOrderIDs = updationItemItems
      .filter(item => item.status === OrderVerifyStatus.updationInProgress)
      .map(item => item?.order?.id);
    if (newOrderIDs.length) checkAsyncOrderStatus(newOrderIDs);
  }, [checkAsyncOrderStatus, updationItemItems]);

  const onSubmit = useCallback(
    (order: Order, status: OrderVerifyStatus) => {
      const mixpanelEventParams = {
        type: EventType.cta,
        client: ClientType.meijer,
        legacyId: order?.id,
        runId: routeParams?.runId,
        countOfIems: order?.items?.length,
        storeType: getStoreName(String(routeParams?.storeId)),
      };
      if (status !== OrderVerifyStatus.updationFailed) {
        Analytics.eventWithProps('order scan', mixpanelEventParams);
        return b2bNav.B2BBarcode({
          runId: routeParams?.runId,
          storeId: routeParams?.storeId,
        });
      }
      Analytics.eventWithProps('return back all items', mixpanelEventParams);
      b2bNav.B2BOrderInfo({
        runId: routeParams?.runId,
        screenTitle: title?.returnOrder,
        modes: OrderInfoModes.orderReturn,
        orderId: order?.id,
      });
    },
    [routeParams?.runId, routeParams?.storeId, title?.returnOrder],
  );

  const navigateToBarcode = useCallback(
    (item: OrdersForUpdation) => {
      const mixpanelEventParams = {
        type: EventType.cta,
        client: ClientType.meijer,
        legacyId: item?.order?.id,
        runId: routeParams?.runId,
        countOfItems: item?.order?.items?.length,
        storeType: getStoreName(String(routeParams?.storeId)),
      };
      Analytics.eventWithProps('order re-scan', mixpanelEventParams);
      return b2bNav.B2BBarcode({
        runId: routeParams?.runId,
        storeId: routeParams?.storeId,
        orderLegacyIds: [item?.order?.id],
      });
    },
    [routeParams?.runId, routeParams?.storeId],
  );

  const renderItem = useCallback(
    ({item}: {item: OrdersForUpdation}) => {
      const getRightComponent = () => {
        if (item.status === OrderVerifyStatus.scanSuccessful)
          return (
            <Touchable onPress={() => navigateToBarcode(item)}>
              <CustomText
                style={[
                  styles.scanAgain,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[700].normal,
                  },
                ]}>
                Scan again
              </CustomText>
            </Touchable>
          );
      };

      return (
        <CheckoutItem
          buttonTitle={
            item.status === OrderVerifyStatus.updationDone
              ? button?.scanBarcode
              : button?.returnItems
          }
          hideItemButton={
            !showButton.includes(item.status) ||
            Boolean(
              (item.status === OrderVerifyStatus.updationDone &&
                (cancelledOrderIds?.length || isScannedEnabled)) ||
                validationItems?.length,
            )
          }
          onButtonClick={() => onSubmit(item?.order, item?.status)}
          loadingFooter={{
            title: title?.approvalInProgress,
            subText: body?.etaMinutes,
          }}
          failedFooter={{
            title: title?.approvalFailed,
            subText: body?.orderCancelled,
          }}
          order={item.order}
          status={item.status}
          successFooterText={
            item.status === OrderVerifyStatus.scanSuccessful
              ? body?.barcodeScanSuccess
              : body?.approvalSuccessful
          }
          btnLoadingTitle={body?.finishCancelOrders}
          leftComponent={
            <If condition={item.status === OrderVerifyStatus.updationDone}>
              <QRCode
                fill={theme?.background.blue[100]}
                viewBox="-5 -2 22 17"
                style={styles.icon}
              />
            </If>
          }
          footerRightComponent={getRightComponent()}
        />
      );
    },
    [
      body?.approvalSuccessful,
      body?.barcodeScanSuccess,
      body?.etaMinutes,
      body?.finishCancelOrders,
      body?.orderCancelled,
      button?.returnItems,
      button?.scanBarcode,
      cancelledOrderIds?.length,
      isScannedEnabled,
      muliFont,
      navigateToBarcode,
      onSubmit,
      showButton,
      theme?.background.blue,
      title?.approvalFailed,
      title?.approvalInProgress,
      theme?.text.blue,
      validationItems?.length,
    ],
  );

  const toggleModal = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const OnButtonPress = useCallback(() => {
    if (scanFinished)
      return navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.VanLoadSite,
        params: {runId: routeParams?.runId},
      });
    toggleModal();
  }, [routeParams?.runId, scanFinished, toggleModal]);

  const toNavigate = useCallback(() => {
    toggleModal();
    if (cancelledOrderIds.length === updationItemItems.length) {
      navigation.reset([
        {
          name: MainScreens.Tabs,
          params: {
            screen: TabScreens.YourSchedule,
            params: {},
          },
        },
      ]);
      return;
    }
    getOrdersForValidationAndUpdation(routeParams?.runId ?? 0);
  }, [
    cancelledOrderIds.length,
    getOrdersForValidationAndUpdation,
    routeParams?.runId,
    toggleModal,
    updationItemItems.length,
  ]);

  const onCancel = useCallback(async () => {
    setCancelLoading(true);
    await Promise.all(
      cancelledOrderIds?.map(
        async id => await cancelOrder(routeParams?.runId ?? 0, id),
      ),
    );
    toNavigate();
    setCancelLoading(false);
  }, [cancelOrder, cancelledOrderIds, routeParams?.runId, toNavigate]);

  useEffect(() => {
    const intervalId = setInterval(checkForUpdates, 10000);

    if (!isFocus) clearInterval(intervalId);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocus]);

  const refresh = useCallback(async () => {
    if (isUpdate && reFetchOrderIds?.length)
      await updateB2BOrder(reFetchOrderIds);
    if (routeParams?.runId)
      getOrdersForValidationAndUpdation(routeParams?.runId);
  }, [
    getOrdersForValidationAndUpdation,
    isUpdate,
    reFetchOrderIds,
    routeParams?.runId,
    updateB2BOrder,
  ]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeParams?.runId, isUpdate]);

  return (
    <Box style={styles.container}>
      <If condition={cancelledOrderIds?.length}>
        <HBox
          style={[
            styles.infoBox,
            {
              backgroundColor: theme?.background.red[200],
            },
          ]}>
          <H5Text
            style={[
              styles.disclaimerText,
              styles.iconText,
              {
                color: theme?.text?.blue?.[300],
                fontFamily: muliFont[900].normal,
              },
            ]}>
            {body?.approvalFailed?.[0]}
          </H5Text>
          <H6Text
            style={[
              styles.disclaimerText,
              {
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {body?.approvalFailed?.[1]}
          </H6Text>
        </HBox>
      </If>
      <If condition={loading}>
        <ActivityIndicator size={'large'} style={styles.flexStyle} />
      </If>
      <If condition={!loading}>
        <CustomFlatList
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listSTyle]}
          refreshControl={
            (
              <RefreshControl
                refreshing={statusState.loading}
                onRefresh={refresh}
                tintColor={theme?.background.gray[100]}
                style={styles.refreshControl}
              />
            ) as React.ReactElement<RefreshControlProps>
          }
          renderItem={renderItem}
          data={updationItemItems}
          extraData={updationItemItems}
          onScroll={props?.onScroll}
          ListEmptyComponent={
            <Box style={styles.flexStyle}>
              <EmptyOrders bottomText={body?.noOrdersAvailable} />
            </Box>
          }
        />
        <If condition={cancelledOrderIds?.length || scanFinished}>
          <Box
            style={[
              styles.bottomDivider,
              {
                borderTopColor: theme?.background.gray[200],
              },
            ]}
          />
          <PButton
            title={!scanFinished ? button?.cancelOrder : button?.transferOrder}
            style={styles.buttonStyle}
            bgColor={theme?.background.blue[100]}
            disabledBgColor={'transparent'}
            titleDisableColor={theme?.text.blue[100]}
            borderDisableColor={theme?.text.blue[100]}
            disabled={cancelLoading || statusState.loading}
            borderColor={theme?.background.blue[100]}
            borderWidth={1}
            isLoading={cancelLoading}
            changeStyleOnLoading
            loadingText={body?.cancelOrders}
            onPress={OnButtonPress}
            loadingIndicator={<ActivityIndicator />}
          />
        </If>
        <If condition={modal}>
          <CancelOrderModal
            open={modal}
            onClose={toggleModal}
            onSubmit={onCancel}
            loading={cancelLoading || statusState.loading}
          />
        </If>
      </If>
    </Box>
  );
};

export default React.memo(Approval);
